import React, { useEffect, useMemo, useState } from 'react';
import { Sparkles, Info } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const FRIEDRICH_MEDIA_BASE = 'https://shop.friedrich-motorsport.de';

function formatPrice(v) {
  if (v == null) return '0,00 €';
  return Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

/**
 * Friedrich Motorsport Konfigurator — v253 Fallback-Preview.
 *
 * Wird nur angezeigt, wenn ein Parent-Produkt `has_customizer=true` hat,
 * aber noch KEINE Kind-Varianten materialisiert wurden (Admin-Vorschau).
 * Sobald die Varianten via /api/admin/friedrich/materialize erzeugt und live
 * geschaltet sind, uebernehmen die nativen PDP-Multi-Dim-Dropdowns
 * (Material x Endrohr x Schuerze) — dieses Komponent wird dann ausgeblendet.
 */
export default function FriedrichKonfigurator({ product }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMaterial, setSelectedMaterial] = useState('');

  useEffect(() => {
    if (!product?.alias) return;
    setLoading(true);
    fetch(`${API}/api/admin/friedrich/public/configurator/${product.alias}`)
      .then(r => (r.ok ? r.json() : Promise.reject(r)))
      .then(data => setConfig(data))
      .catch(() => setConfig(null))
      .finally(() => setLoading(false));
  }, [product?.alias]);

  const basePrice = config?.base_price || product?.price || 0;
  const materials = config?.materials || [];
  const endrohre = config?.endrohre || [];
  const bumperOpts = config?.bumper_options || [];

  const filteredEndrohre = useMemo(() => {
    if (!selectedMaterial) return endrohre;
    return endrohre.filter(o => o.material === selectedMaterial);
  }, [endrohre, selectedMaterial]);

  if (!product?.has_customizer) return null;
  if (loading) {
    return (
      <div
        className="fried-konfig fried-konfig--loading"
        data-testid="friedrich-konfig-loading"
        style={{ marginTop: '1.5rem', padding: '1rem', color: '#666' }}
      >
        Konfigurator wird geladen…
      </div>
    );
  }
  if (!config || endrohre.length === 0) return null;

  return (
    <section
      className="fried-konfig"
      data-testid="friedrich-konfigurator"
      style={{
        marginTop: '1.5rem',
        padding: '1.25rem',
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 12,
      }}
    >
      <header style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
        <Sparkles size={18} strokeWidth={2.2} />
        <h3 style={{ margin: 0, fontSize: '1.15rem' }}>Konfigurator-Vorschau</h3>
      </header>
      <div
        data-testid="friedrich-konfig-info"
        style={{
          display: 'flex', gap: 8, alignItems: 'flex-start',
          padding: '0.75rem 1rem', marginBottom: '1rem',
          background: 'rgba(245,158,11,0.08)',
          border: '1px solid rgba(245,158,11,0.35)',
          borderRadius: 8, fontSize: '0.85rem', color: '#c25b00',
        }}
      >
        <Info size={16} style={{ flexShrink: 0, marginTop: 2 }} />
        <div>
          <strong>Hinweis:</strong> Kind-Varianten wurden fuer dieses Produkt noch nicht
          materialisiert. Nach Materialisierung erscheinen die 3 Auswahl-Dropdowns
          (<em>Material</em>, <em>Endrohr</em>, <em>Schuerze</em>) automatisch oben.
        </div>
      </div>

      {/* Material-Filter (rein visuell) */}
      {materials.length > 1 && (
        <div className="fried-konfig-material-row" data-testid="fried-konfig-material" style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: 6 }}>
            Material:
          </label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => setSelectedMaterial('')}
              data-testid="fried-material-all"
              style={{
                padding: '6px 12px', fontSize: '0.85rem',
                background: selectedMaterial === '' ? '#f59e0b' : 'rgba(255,255,255,0.05)',
                color: selectedMaterial === '' ? '#000' : 'inherit',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 999, cursor: 'pointer',
              }}
            >
              Alle
            </button>
            {materials.map(m => (
              <button
                key={m.value}
                type="button"
                onClick={() => setSelectedMaterial(m.value)}
                data-testid={`fried-material-${m.value}`}
                style={{
                  padding: '6px 12px', fontSize: '0.85rem',
                  background: selectedMaterial === m.value ? '#f59e0b' : 'rgba(255,255,255,0.05)',
                  color: selectedMaterial === m.value ? '#000' : 'inherit',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 999, cursor: 'pointer',
                }}
              >
                {m.value}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Endrohr-Grid (Vorschau) */}
      <div
        className="fried-konfig-grid"
        data-testid="friedrich-endrohre-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
          gap: '.6rem',
          maxHeight: 320,
          overflowY: 'auto',
        }}
      >
        {filteredEndrohre.slice(0, 60).map((opt, idx) => {
          const price = basePrice + 2 * Number(opt.surcharge || 0);
          const imgSrc = opt.image_url
            ? (opt.image_url.startsWith('http') ? opt.image_url : FRIEDRICH_MEDIA_BASE + opt.image_url)
            : '';
          return (
            <div
              key={opt.sub_sku || idx}
              className="fried-konfig-item"
              data-testid={`friedrich-endrohr-${opt.sub_sku || idx}`}
              style={{
                padding: '.6rem',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 8,
                display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.8rem',
              }}
            >
              {imgSrc && (
                <div style={{
                  height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: '#fff', borderRadius: 4,
                }}>
                  <img
                    src={imgSrc}
                    alt={opt.label}
                    loading="lazy"
                    style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                  />
                </div>
              )}
              <div style={{ lineHeight: 1.3 }}>{opt.label}</div>
              <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>{opt.material} · {opt.sub_sku}</div>
              <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                {formatPrice(price)}
                {opt.surcharge > 0 && (
                  <span style={{ fontSize: '0.7rem', color: '#c25b00', marginLeft: 6 }}>
                    +{formatPrice(2 * opt.surcharge)}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {filteredEndrohre.length > 60 && (
        <p style={{ marginTop: '0.75rem', fontSize: '0.75rem', opacity: 0.65 }}>
          … {filteredEndrohre.length - 60} weitere Optionen (nach Materialisierung im Dropdown verfuegbar)
        </p>
      )}

      {bumperOpts.length > 0 && (
        <p style={{ marginTop: '0.75rem', fontSize: '0.85rem', opacity: 0.8 }}>
          <strong>Schuerze:</strong> {bumperOpts.map(b => b.label).join(', ')}
        </p>
      )}
    </section>
  );
}
